import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BadgePercent, MapPin, CalendarDays, Martini, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useLanguage } from "@/i18n/LanguageContext";

const FEATURE_ICONS = [MapPin, CalendarDays, Martini];

export const AdBanner = () => {
  const [ads, setAds] = useState([]);
  const [idx, setIdx] = useState(0);
  const { t } = useLanguage();

  useEffect(() => {
    api.get("/ads/active").then((r) => setAds(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (ads.length < 2) return undefined;
    const iv = setInterval(() => setIdx((i) => (i + 1) % ads.length), 8000);
    return () => clearInterval(iv);
  }, [ads.length]);

  if (ads.length === 0) return null;
  const ad = ads[idx % ads.length];

  return (
    <section className="mx-auto mt-4 max-w-[1800px] px-4 sm:mt-5 sm:px-6 lg:px-10" data-testid="ad-banner-section">
      <AnimatePresence mode="wait">
        <motion.a
          key={ad.id}
          href={ad.link || undefined}
          target={ad.link ? "_blank" : undefined}
          rel="noreferrer sponsored"
          data-testid="ad-banner"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="group relative block overflow-hidden rounded-3xl shadow-[0_8px_30px_rgba(15,23,42,0.18)]"
        >
          <img
            src={ad.image}
            alt={ad.title}
            loading="lazy"
            style={{ filter: "contrast(1.07) saturate(1.18)" }}
            className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/60 to-slate-950/20" />

          <span
            data-testid="ad-badge"
            className="absolute left-4 top-4 flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1 text-[11px] font-bold text-slate-700 shadow"
          >
            <BadgePercent className="h-3.5 w-3.5 text-[#2563EB]" />
            {t("Publicité")}
          </span>

          <div className="relative flex flex-col gap-5 p-6 sm:flex-row sm:items-center sm:gap-8 sm:p-8 lg:px-12">
            {ad.image && (
              <img
                src={ad.image}
                alt=""
                style={{ filter: "contrast(1.07) saturate(1.18)" }}
                className="hidden h-24 w-24 shrink-0 rounded-2xl border-2 border-white/20 object-cover shadow-lg sm:block"
              />
            )}
            <div className="min-w-0 flex-1">
              <h3 className="text-2xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">{ad.title}</h3>
              {ad.tagline && (
                <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-white/85">{ad.tagline}</p>
              )}
              {ad.slogan && (
                <p className="mt-2 text-lg font-semibold italic text-amber-300 sm:text-xl">{ad.slogan}</p>
              )}
              {ad.features.length > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5">
                  {ad.features.map((f, i) => {
                    const Icon = FEATURE_ICONS[i % FEATURE_ICONS.length];
                    return (
                      <span key={f} className="flex items-center gap-1.5 text-xs font-medium text-white/90">
                        <Icon className="h-3.5 w-3.5 text-amber-300" />
                        {f}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
            {ad.link && (
              <span
                data-testid="ad-cta"
                className="flex w-fit shrink-0 items-center gap-2 rounded-full bg-amber-400 px-6 py-3 text-sm font-extrabold text-slate-900 shadow-lg transition group-hover:bg-amber-300 group-hover:shadow-amber-400/30 sm:px-7"
              >
                {t("Découvrir")}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </span>
            )}
          </div>
        </motion.a>
      </AnimatePresence>
    </section>
  );
};
